import React, { useMemo } from 'react';
import { CreditCard, Building2, Wallet, Globe, ShieldCheck } from 'lucide-react';
import { BRAND_LOGOS, BrandLogoItem } from '../data/brandLogos';
import { DynamicPaymentGateway } from '../types';

interface PaymentBrandBadgeProps {
  paymentMethod?: string;
  logoIcon?: string;
  gateways?: DynamicPaymentGateway[];
  className?: string;
  iconOnly?: boolean;
  showName?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const PaymentBrandBadge: React.FC<PaymentBrandBadgeProps> = ({
  paymentMethod = '',
  logoIcon,
  gateways = [],
  className = '',
  iconOnly = false,
  showName = true,
  size = 'md'
}) => {
  // Resolve logo URL / Icon
  const resolvedBrand = useMemo(() => {
    // 1. Direct logoIcon check if provided or matched via gateways list
    let targetIcon = logoIcon;
    let targetName = paymentMethod;

    if (!targetIcon && paymentMethod && gateways.length > 0) {
      const matchedGw = gateways.find(
        (g) =>
          g.name.toLowerCase() === paymentMethod.toLowerCase() ||
          paymentMethod.toLowerCase().includes(g.id.toLowerCase()) ||
          g.id.toLowerCase().includes(paymentMethod.toLowerCase())
      );
      if (matchedGw) {
        targetIcon = matchedGw.logoIcon;
        targetName = matchedGw.name;
      }
    }

    // If explicit URL or data URI
    if (targetIcon && (targetIcon.startsWith('data:') || targetIcon.startsWith('http') || targetIcon.startsWith('/'))) {
      return {
        type: 'url' as const,
        url: targetIcon,
        name: targetName || 'Payment Method'
      };
    }

    // Check by ID or logoIcon key in BRAND_LOGOS
    if (targetIcon) {
      const match = BRAND_LOGOS.find(
        (b) => b.id.toLowerCase() === targetIcon?.toLowerCase() || b.name.toLowerCase() === targetIcon?.toLowerCase()
      );
      if (match) {
        return {
          type: 'preset' as const,
          url: match.logoUrl,
          name: match.name
        };
      }
    }

    // Smart Auto Detection by matching paymentMethod name against brand keywords
    if (paymentMethod) {
      const lowerMethod = paymentMethod.toLowerCase();
      const smartMatch = BRAND_LOGOS.find((b) => {
        if (b.id.toLowerCase() === lowerMethod || b.name.toLowerCase().includes(lowerMethod) || lowerMethod.includes(b.name.toLowerCase())) {
          return true;
        }
        return b.keywords?.some((k) => lowerMethod.includes(k.toLowerCase()));
      });

      if (smartMatch) {
        return {
          type: 'preset' as const,
          url: smartMatch.logoUrl,
          name: smartMatch.name
        };
      }
    }

    return null;
  }, [paymentMethod, logoIcon, gateways]);

  const sizeClasses = {
    sm: { img: 'w-4 h-4', text: 'text-[10px]', badge: 'px-2 py-0.5 gap-1.5' },
    md: { img: 'w-5 h-5', text: 'text-xs', badge: 'px-2.5 py-1 gap-2' },
    lg: { img: 'w-6 h-6', text: 'text-sm', badge: 'px-3 py-1.5 gap-2.5' }
  }[size];

  const renderFallback = () => {
    const lower = paymentMethod.toLowerCase();
    if (lower.includes('bank') || lower.includes('wire') || lower.includes('swift')) {
      return <Building2 className={`${sizeClasses.img} text-cyan-400`} />;
    }
    if (lower.includes('crypto') || lower.includes('binance') || lower.includes('usdt')) {
      return <Globe className={`${sizeClasses.img} text-amber-400`} />;
    }
    if (lower.includes('wallet') || lower.includes('skrill')) {
      return <Wallet className={`${sizeClasses.img} text-purple-400`} />;
    }
    return <CreditCard className={`${sizeClasses.img} text-slate-400`} />;
  };

  if (iconOnly) {
    if (resolvedBrand) {
      return (
        <img
          src={resolvedBrand.url}
          alt={resolvedBrand.name}
          className={`${sizeClasses.img} object-contain rounded-md shrink-0 ${className}`}
          onError={(e) => {
            (e.target as HTMLElement).style.display = 'none';
          }}
        />
      );
    }
    return renderFallback();
  }

  return (
    <span
      className={`inline-flex items-center rounded-lg bg-slate-900/90 border border-slate-800/80 font-bold text-slate-200 ${sizeClasses.badge} ${className}`}
    >
      {resolvedBrand ? (
        <img
          src={resolvedBrand.url}
          alt={resolvedBrand.name}
          className={`${sizeClasses.img} object-contain rounded-md shrink-0`}
          onError={(e) => {
            (e.target as HTMLElement).style.display = 'none';
          }}
        />
      ) : (
        renderFallback()
      )}
      {showName && <span className={`${sizeClasses.text} truncate`}>{paymentMethod || resolvedBrand?.name || 'Payment'}</span>}
    </span>
  );
};
